import * as React from 'react';
import { AccessPanel, type AccessEntry } from '@/components/common/access-panel';
import { useMutation } from '@tanstack/react-query';
import { Database, FolderOpen, Pencil, Plus, Trash2 } from 'lucide-react';
import { DataTable, type Column } from '@/components/common/data-table';
import { MountedProjects, ProjectMountsSheet } from '@/components/common/project-mounts';
import { EmptyState } from '@/components/common/states';
import { OwnerTransfer, ResourceOwner } from '@/components/common/owner';
import { SearchInput } from '@/components/common/search-input';
import { useConfirm } from '@/components/common/confirm-dialog';
import { SectionHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardHeaderText, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Field } from '@/components/ui/field';
import { Input, Textarea } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toast';
import {
  useDatasetAccess,
  useDatasets,
  useDatasetUsage,
  useOrganizations,
  qk,
  useInvalidate, useCan } from '@/lib/api/queries';
import { datasetsApi, deletionCostApi } from '@/lib/api/endpoints';
import { useI18n, useT } from '@/lib/i18n';
import { formatBytes, formatNumber, formatRelative } from '@/lib/format';
import { DatasetExplorer } from './dataset-explorer';
import type { Dataset } from '@/lib/api/types';

function CreateDatasetSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  const toast = useToast();
  const invalidate = useInvalidate();
  const can = useCan();

  const [mode, setMode] = React.useState<'local' | 'external'>('local');
  const [classification, setClassification] = React.useState('non-hds');
  // Une donnee reglementee appartient a une organisation, jamais a une
  // personne : la plateforme le refuse desormais, et le formulaire demande
  // laquelle plutot que de laisser l'enregistrement echouer.
  const [ownerId, setOwnerId] = React.useState('');
  const organizations = useOrganizations();
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [endpoint, setEndpoint] = React.useState('');
  const [region, setRegion] = React.useState('');
  const [bucket, setBucket] = React.useState('');
  const [prefix, setPrefix] = React.useState('');
  const [accessKey, setAccessKey] = React.useState('');
  const [secretKey, setSecretKey] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setBucket('');
      setAccessKey('');
      setSecretKey('');
      setTouched(false);
    }
  }, [open]);

  const nameError = touched && !name.trim() ? t('common.required') : undefined;
  const bucketError = touched && mode === 'external' && !bucket.trim() ? t('common.required') : undefined;
  const valid =
    Boolean(name.trim()) &&
    (mode === 'local' || bucket.trim()) &&
    (classification !== 'hds' || Boolean(ownerId));

  const mutation = useMutation({
    mutationFn: () =>
      datasetsApi.create({
        name: name.trim(),
        description: description.trim(),
        classification,
        ...(classification === 'hds' ? { ownerId } : {}),
        ...(mode === 'external'
          ? {
              provider: 's3',
              endpoint: endpoint.trim(),
              region: region.trim(),
              bucket: bucket.trim(),
              prefix: prefix.trim(),
              accessKey,
              secretKey,
            }
          : {}),
      }),
    onSuccess: () => {
      invalidate(qk.datasets);
      onOpenChange(false);
      toast.success(name.trim(), t('datasets.create'));
    },
    onError: (error) => toast.error(error, t('datasets.createTitle')),
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent size="lg" aria-describedby={undefined}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setTouched(true);
            if (valid) mutation.mutate();
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <SheetHeader>
            <SheetTitle>{t('datasets.createTitle')}</SheetTitle>
            <SheetDescription>{t('datasets.createHint')}</SheetDescription>
          </SheetHeader>

          <SheetBody>
            <Field label={t('datasets.modeLabel')}>
              <Select
                value={mode}
                onValueChange={(value) => setMode(value === 'external' ? 'external' : 'local')}
                options={[
                  { value: 'local', label: t('datasets.modeLocal'), hint: t('datasets.modeLocalHint') },
                  {
                    value: 'external',
                    label: t('datasets.modeExternal'),
                    hint: t('datasets.modeExternalHint'),
                  },
                ]}
              />
            </Field>

            <Field label={t('datasets.createTitle')} error={nameError} required htmlFor="dataset-name">
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                onBlur={() => setTouched(true)}
                maxLength={120}
                autoFocus
              />
            </Field>

            <Field label={t('common.description')}>
              <Textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={2}
                maxLength={500}
              />
            </Field>

            {/* Registering regulated data is a box in the creation matrix
                (ADR-049), so the control follows the capability the platform
                answered - a data steward sees it, a user does not - rather
                than the administrator role read off the token. */}
            {can('register-regulated-dataset') ? (
              <Field
                label={t('datasets.classificationLabel')}
                description={t('datasets.classificationHint')}
              >
                <Select
                  value={classification}
                  onValueChange={setClassification}
                  options={[
                    { value: 'non-hds', label: t('datasets.classificationStandard') },
                    { value: 'hds', label: t('datasets.classificationHds') },
                  ]}
                />
              </Field>
            ) : null}

            {classification === 'hds' ? (
              <>
                <Field label={t('datasets.ownerOrganizationLabel')} required>
                  <Select
                    value={ownerId}
                    onValueChange={setOwnerId}
                    placeholder={t('datasets.ownerOrganizationPlaceholder')}
                    options={(organizations.data ?? []).map((organization) => ({
                      value: organization.id,
                      label: organization.name,
                    }))}
                  />
                </Field>
                <p className="rounded-md border border-warning/40 bg-warning-subtle px-3 py-2 text-xs leading-relaxed text-warning-foreground">
                  {t('datasets.hdsWarning')}
                </p>
              </>
            ) : null}

            {mode === 'external' ? (
              <>
                <Field label={t('datasets.endpointLabel')} required>
                  <Input
                    value={endpoint}
                    onChange={(event) => setEndpoint(event.target.value)}
                    placeholder="https://s3.example.com"
                    inputMode="url"
                  />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={t('datasets.bucketLabel')} error={bucketError} required>
                    <Input value={bucket} onChange={(event) => setBucket(event.target.value)} />
                  </Field>
                  <Field label={t('common.region')}>
                    <Input
                      value={region}
                      onChange={(event) => setRegion(event.target.value)}
                      placeholder="eu-west-3"
                    />
                  </Field>
                </div>
                <Field label={t('common.prefix')} description={t('datasets.prefixHint')}>
                  <Input value={prefix} onChange={(event) => setPrefix(event.target.value)} />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={t('common.accessKey')} required>
                    <Input
                      value={accessKey}
                      onChange={(event) => setAccessKey(event.target.value)}
                      autoComplete="off"
                    />
                  </Field>
                  <Field label={t('common.secretKey')} required>
                    <Input
                      type="password"
                      value={secretKey}
                      onChange={(event) => setSecretKey(event.target.value)}
                      autoComplete="new-password"
                    />
                  </Field>
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {t('datasets.replaceCredentialsHint')}
                </p>
              </>
            ) : null}
          </SheetBody>

          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={mutation.isPending} disabled={!valid}>
              {t('common.create')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Handing a dataset over.
 *
 * The API has accepted a new owner since the first day and no screen ever
 * asked for one, so in practice a dataset belonged to whoever created it, for
 * good - and a bucket that belongs to a person who leaves is a bucket nobody
 * can administer.
 */
/**
 * How big a dataset is.
 *
 * The catalogue showed a bucket path under "storage" and no size anywhere, so
 * the first question anybody asks about a dataset had no answer. Measuring
 * means listing the bucket - 24,179 objects for HDS-For - so it is asked for
 * per row, cached, and shows a dash rather than a zero while it is unknown: a
 * confident "0 B" on a 400 GB dataset is worse than no answer.
 */
function DatasetSize({ dataset }: { dataset: Dataset }) {
  const { locale } = useI18n();
  const usage = useDatasetUsage(dataset.id);
  if (usage.isLoading) return <span className="text-xs text-muted-foreground">…</span>;
  if (usage.isError || !usage.data) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="text-xs tabular-nums text-muted-foreground">
      {formatBytes(usage.data.totalBytes, locale)}
      <span className="ml-1 opacity-70">
        ({formatNumber(usage.data.objects, locale)}
        {usage.data.truncated ? '+' : ''})
      </span>
    </span>
  );
}

/**
 * Remplacer les identifiants d'acces a l'objet store.
 *
 * Ils ne pouvaient etre poses qu'a la creation du dataset. Le jour ou le mot
 * de passe du fournisseur a tourne, chaque dataset portant sa propre copie
 * chiffree, ils ont cesse d'etre lisibles en meme temps - et la seule issue
 * etait de les recreer, en perdant les droits deja accordes dessus.
 *
 * Le formulaire ne montre jamais les identifiants en place. Les reafficher
 * n'aiderait personne a les remplacer et ferait de cet ecran un second endroit
 * d'ou ils peuvent fuiter.
 */
/* Corriger le nom ou la description d'un jeu de donnees.
 *
 *  Le serveur accepte ces deux champs depuis toujours ; aucun ecran ne les
 *  appelait, donc renommer un dataset demandait un appel API a la main. Le
 *  client web declarait meme la methode sans jamais s'en servir.
 *
 *  L'adresse, le bucket et le fournisseur sont montres verrouilles plutot
 *  qu'absents. C'est la premiere chose que quelqu'un vient chercher ici quand
 *  un fournisseur S3 change, et un champ manquant se lit comme un oubli la ou
 *  un champ verrouille dit que le serveur ne sait pas encore le faire. */
function EditDatasetSheet({
  dataset,
  open,
  onOpenChange,
}: {
  dataset: Dataset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');

  // Repart de ce que le dataset porte a chaque ouverture, jamais d'une saisie
  // laissee en place a la fermeture precedente.
  React.useEffect(() => {
    if (!open || !dataset) return;
    setName(dataset.name ?? '');
    setDescription(dataset.description ?? '');
  }, [open, dataset]);

  const save = useMutation({
    mutationFn: () =>
      datasetsApi.update(dataset!.id, { name: name.trim(), description: description.trim() }),
    onSuccess: () => {
      invalidate(qk.datasets);
      toast.success(name.trim(), t('datasets.editTitle'));
      onOpenChange(false);
    },
    onError: (error) => toast.error(error, t('datasets.editTitle')),
  });

  if (!dataset) return null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent aria-describedby={undefined}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) save.mutate();
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <SheetHeader>
            <SheetTitle>{t('datasets.editTitle')}</SheetTitle>
            <SheetDescription>{t('datasets.editHint')}</SheetDescription>
          </SheetHeader>

          <SheetBody>
            <Field label={t('common.name')} required>
              <Input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
            </Field>
            <Field label={t('common.description')}>
              <Textarea
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Field>
            <Field label={t('datasets.storageFixed')} description={t('datasets.storageFixedHint')}>
              <Input
                value={[dataset.endpoint, dataset.bucket].filter(Boolean).join(' / ') || '—'}
                disabled
                readOnly
                className="font-mono text-xs"
              />
            </Field>
          </SheetBody>

          <SheetFooter>
            <Button type="submit" variant="primary" disabled={!name.trim()} loading={save.isPending}>
              {t('common.save')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function DatasetCredentials({ dataset }: { dataset: Dataset }) {
  const t = useT();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [accessKey, setAccessKey] = React.useState('');
  const [secretKey, setSecretKey] = React.useState('');

  const replace = useMutation({
    mutationFn: () => datasetsApi.setCredentials(dataset.id, { accessKey, secretKey }),
    onSuccess: () => {
      setAccessKey('');
      setSecretKey('');
      invalidate(qk.datasets);
      toast.success(t('datasets.credentialsReplaced'));
    },
    // L'erreur du serveur telle quelle : elle distingue "ces identifiants
    // n'ouvrent pas le bucket" d'une panne, et c'est la difference entre
    // corriger une faute de frappe et ouvrir un ticket.
    onError: (error) => toast.error(error, t('datasets.replaceCredentials')),
  });

  if (dataset.provider === 'minio') return null;

  return (
    <Card>
      <CardHeader>
        <CardHeaderText>
          <CardTitle>{t('datasets.replaceCredentials')}</CardTitle>
          <CardDescription>{t('datasets.replaceCredentialsHint')}</CardDescription>
        </CardHeaderText>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            replace.mutate();
          }}
        >
          <Field label={t('datasets.accessKey')}>
            <Input value={accessKey} onChange={(event) => setAccessKey(event.target.value)} autoComplete="off" />
          </Field>
          <Field label={t('common.secretKey')}>
            <Input
              type="password"
              value={secretKey}
              onChange={(event) => setSecretKey(event.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <Button
            type="submit"
            variant="secondary"
            loading={replace.isPending}
            disabled={!accessKey.trim() || !secretKey.trim()}
          >
            {t('datasets.replaceCredentials')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function DatasetOwnership({ dataset }: { dataset: Dataset }) {
  const t = useT();
  const toast = useToast();
  const invalidate = useInvalidate();

  const transfer = useMutation({
    mutationFn: (input: { ownerType: string; ownerId: string }) =>
      datasetsApi.setOwner(dataset.id, input),
    onSuccess: () => {
      invalidate(qk.datasets);
      toast.success(t('projects.transferOwnership'));
    },
    onError: (error) => toast.error(error, t('projects.transferOwnership')),
  });

  return (
    <Card>
      <CardHeader>
        <CardHeaderText>
          <CardTitle>{t('projects.transferOwnership')}</CardTitle>
          <CardDescription>{t('datasets.ownershipHint')}</CardDescription>
        </CardHeaderText>
      </CardHeader>
      <CardContent>
        <OwnerTransfer
          owner={dataset}
          pending={transfer.isPending}
          onTransfer={(input) => transfer.mutate(input)}
          label={t('projects.transferOwnership')}
        />
      </CardContent>
    </Card>
  );
}

function DatasetPermissions({ dataset }: { dataset: Dataset }) {
  const t = useT();
  const toast = useToast();
  const invalidate = useInvalidate();
  const access = useDatasetAccess(dataset.id);
  const grant = useMutation({
    mutationFn: (input: { subjectType: string; subjectId: string; role: string }) => datasetsApi.grant(dataset.id, input.subjectType, input.subjectId, input.role),
    onSuccess: () => invalidate(qk.datasetAccess(dataset.id)),
    onError: (error) => toast.error(error, t('access.grant')),
  });
  const revoke = useMutation({
    mutationFn: (entry: AccessEntry) => datasetsApi.revoke(dataset.id, entry.subjectType, entry.subjectId),
    onSuccess: () => invalidate(qk.datasetAccess(dataset.id)),
    onError: (error) => toast.error(error, t('access.revoke')),
  });
  return (
    <AccessPanel
      title={t('access.title')}
      hint={t('datasets.permissionsHint')}
      owner={{ type: dataset.ownerType, id: dataset.ownerId || dataset.ownerUserId, name: dataset.ownerName }}
      entries={access.data}
      isLoading={access.isLoading}
      isError={access.isError}
      error={access.error}
      onRetry={() => void access.refetch()}
      
      onGrant={(subjectType, subjectId, role) => grant.mutate({ subjectType, subjectId, role })}
      granting={grant.isPending}
      onRevoke={(entry) => revoke.mutate(entry)}
    />
  );
}

export function DatasetCatalog({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (datasetId: string | null) => void;
}) {
  const t = useT();
  const can = useCan();
  const { locale } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidate();
  const { dialog, ask } = useConfirm();

  const datasets = useDatasets();
  const [search, setSearch] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<Dataset | null>(null);
  const [mounted, setMounted] = React.useState<Dataset | null>(null);

  /* Ce qu'une suppression de dataset laisse derriere elle.
   *
   *  Rien ne cascade, et c'est justement le probleme : les ontologies qui
   *  decrivent ce bucket lui survivent et continuent de le decrire. Elles
   *  restent au catalogue, elles ont l'air utilisables, et le premier signe
   *  est un scan qui ne retrouve pas sa source. */
  const demanderSuppression = async (dataset: Dataset) => {
    let cout: Awaited<ReturnType<typeof deletionCostApi.dataset>> | null;
    try {
      cout = await deletionCostApi.dataset(dataset.id);
    } catch {
      cout = null;
    }
    const ontologies = cout?.ontologies ?? 0;
    const extraits = cout?.extracts ?? 0;
    ask({
      title: t('datasets.deleteTitle'),
      description:
        ontologies > 0
          ? t('datasets.deleteWarningOntologies', {
              ontologies: String(ontologies),
              extracts: String(extraits),
            })
          : t('datasets.deleteWarning'),
      confirmLabel: t('common.delete'),
      destructive: true,
      confirmationValue: dataset.name,
      onConfirm: () => remove.mutateAsync(dataset.id),
    });
  };

  const selected = datasets.data?.find((dataset) => dataset.id === selectedId) ?? null;

  const remove = useMutation({
    mutationFn: (datasetId: string) => datasetsApi.remove(datasetId),
    onSuccess: () => {
      invalidate(qk.datasets);
      onSelect(null);
    },
    onError: (error) => toast.error(error, t('datasets.deleteTitle')),
  });

  const columns: Column<Dataset>[] = [
    {
      id: 'name',
      header: t('common.name'),
      sortValue: (dataset) => dataset.name,
      searchValue: (dataset) => `${dataset.name} ${dataset.description} ${dataset.bucket}`,
      cell: (dataset) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{dataset.name}</p>
          {dataset.description ? (
            <p className="truncate text-xs text-muted-foreground">{dataset.description}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: 'classification',
      header: t('datasets.classification'),
      sortValue: (dataset) => dataset.classification,
      cell: (dataset) =>
        dataset.classification === 'hds' ? (
          <Badge tone="warning">{t('datasets.classificationHds')}</Badge>
        ) : (
          <Badge tone="outline">{t('datasets.classificationStandard')}</Badge>
        ),
    },
    {
      id: 'storage',
      header: t('datasets.storage'),
      cell: (dataset) => (
        <span className="truncate font-mono text-xs text-muted-foreground">
          {dataset.bucket}
          {dataset.prefix ? `/${dataset.prefix}` : ''}
        </span>
      ),
    },
    {
      id: 'size',
      header: t('common.size'),
      cell: (dataset) => <DatasetSize dataset={dataset} />,
    },
    {
      id: 'owner',
      header: t('common.owner'),
      sortValue: (dataset) => dataset.ownerName || dataset.ownerId,
      // This printed the stored identifier, which is a username for a person
      // and a UUID for an organization - the reason the same organization read
      // as "Imt" on the project list and as 0f2c8a1e-... here.
      cell: (dataset) => <ResourceOwner owner={dataset} />,
    },
    {
      id: 'projects',
      header: t('mounts.title'),
      cell: (dataset) => <MountedProjects kind="dataset" resourceId={dataset.id} />,
    },
    {
      id: 'updatedAt',
      header: t('common.updatedAt'),
      sortValue: (dataset) => dataset.updatedAt,
      cell: (dataset) => (
        <span className="text-xs text-muted-foreground">{formatRelative(dataset.updatedAt, locale)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <SectionHeader
        title={t('datasets.title')}
        description={t('datasets.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <SearchInput
              value={search}
              onValueChange={setSearch}
              label={t('common.search')}
              className="w-56"
            />
            {can('create-dataset') ? (<Button variant="primary" onClick={() => setCreating(true)}>
              <Plus aria-hidden />
              {t('datasets.create')}
            </Button>) : null}
          </div>
        }
      />

      <Card>
        <DataTable
          data={datasets.data}
          columns={columns}
          rowKey={(dataset) => dataset.id}
          isLoading={datasets.isLoading}
          isError={datasets.isError}
          error={datasets.error}
          onRetry={() => void datasets.refetch()}
          search={search}
          onResetSearch={() => setSearch('')}
          onRowClick={(dataset) => onSelect(dataset.id)}
          selectedKey={selectedId}
          defaultSort={{ columnId: 'updatedAt', direction: 'desc' }}
          emptyState={
            <EmptyState
              icon={Database}
              title={t('datasets.empty')}
              description={t('datasets.emptyHint')}
              action={can('create-dataset') ? (<Button variant="primary" onClick={() => setCreating(true)}>
                  <Plus aria-hidden />
                  {t('datasets.create')}
                </Button>) : null}
            />
          }
          rowActions={(dataset) => (
            <>
              <DropdownMenuItem onSelect={() => onSelect(dataset.id)}>{t('common.open')}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setMounted(dataset)}>
                <FolderOpen aria-hidden />
                {t('mounts.title')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setEditing(dataset)}>
                <Pencil aria-hidden />
                {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                destructive
                onSelect={() => void demanderSuppression(dataset)}
              >
                <Trash2 aria-hidden />
                {t('common.delete')}
              </DropdownMenuItem>
            </>
          )}
        />
      </Card>

      {selected ? (
        <>
          <DatasetExplorer dataset={selected} />
          <DatasetCredentials dataset={selected} />
          <DatasetOwnership dataset={selected} />
          <DatasetPermissions dataset={selected} />
        </>
      ) : null}

      <ProjectMountsSheet
        kind="dataset"
        resourceId={mounted?.id ?? null}
        resourceName={mounted?.name}
        open={Boolean(mounted)}
        onOpenChange={(open) => {
          if (!open) setMounted(null);
        }}
      />

      <CreateDatasetSheet open={creating} onOpenChange={setCreating} />
      <EditDatasetSheet
        dataset={editing}
        open={editing !== null}
        onOpenChange={(value) => !value && setEditing(null)}
      />
      {dialog}
    </div>
  );
}
